import { Select, Dropdown, Avatar } from "antd";
import { UserOutlined, LogoutOutlined, SettingOutlined, CalendarOutlined, HomeOutlined, DatabaseOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import Model from "redux/classes/Model";
import Session from "redux/classes/Session";
import { useEffect, useCallback } from "react";
import { useDarkMode, useThemeColors, useAppSelector } from "redux/hooks";
import { ConditionalTooltip } from "./ConditionalTooltip";
import QLogo from "../../../icons/QLogo";
import DLogo from "../../../icons/DLogo";

export function HeaderView(props: { allModels: Model[]; currentModelAlias: string; selectedJobName?: string; session: Session; onLogout: () => void; onSettingsClick: () => void; onScheduleClick: () => void; onQueriesClick: () => void; isLeftPanelCollapsed?: boolean; onExpandLeftPanel?: () => void }) {
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const isAdmin = useAppSelector(state => state.user.settings?.is_admin ?? false);

    useEffect(() => {
        const removeTitle = () => {
            document.querySelectorAll('.model-select .ant-select-selection-item').forEach(node => {
                node.removeAttribute('title');
            });
        };

        removeTitle();
        const container = document.querySelector('.model-select');
        if (!container) return;

        const observer = new MutationObserver(removeTitle);
        observer.observe(container, { childList: true, subtree: true, attributes: true });

        return () => observer.disconnect();
    }, [props.currentModelAlias]);

    const stripPrefix = (name: string) => {
        return name.replace(/^DASHSTREAM\s*-\s*/i, '');
    };

    // Filter models that have dashConfig and pre-process with stripped names
    const dashModels = props.allModels
        .filter(model => model.dashConfig !== undefined && model.dashConfig)
        .map(model => ({
            ...model,
            displayName: stripPrefix(model.name)
        }));

    const handleModelChange = (modelAlias: string) => {
        window.location.href = `/${modelAlias}`;
    };

    const getSisterModelAlias = useCallback((modelAlias: string): string | null => {
        const mappings: { [key: string]: string } = {
            'DASH': 'QST_CONVO',
            'QST_CONVO': 'DASH',
            'QST_MSBU': 'DASH_MSBU',
            'DASH_MSBU': 'QST_MSBU',
            'QST_ONC': 'DASH_ONC',
            'DASH_ONC': 'QST_ONC',
            'QST_OPH': 'DASH_OPH',
            'DASH_OPH': 'QST_OPH',
            'QST_MA': 'DASH_MA',
            'DASH_MA': 'QST_MA',
            'QUERYSTREAM_MMM': 'DASHSTREAM_MMM',
            'DASHSTREAM_MMM': 'QUERYSTREAM_MMM',
            'DASHSTREAM_QC': 'QUERYSTREAM_QC',
            'QUERYSTREAM_QC': 'DASHSTREAM_QC',
            'QUERYSTREAM_JEN_AI': 'DASHSTREAM_JEN_AI',
            'DASHSTREAM_JEN_AI': 'QUERYSTREAM_JEN_AI',
        };
        return mappings[modelAlias] || null;
    }, []);

    const handleSwitchToSister = useCallback(() => {
        const sisterAlias = getSisterModelAlias(props.currentModelAlias);
        if (sisterAlias) {
            window.location.href = `/${encodeURIComponent(sisterAlias)}`;
        }
    }, [props.currentModelAlias, getSisterModelAlias]);

    const sisterModelAlias = getSisterModelAlias(props.currentModelAlias);

    const menuItems: MenuProps['items'] = [
        {
            key: "username",
            label: (
                <div style={{ padding: "8px 12px", fontWeight: "bold", color: themeColors.textColor, pointerEvents: "none" }}>
                    {props.session.userDisplayName}
                </div>
            ),
            disabled: true,
            style: { cursor: "default" },
        },
        ...(isAdmin ? [{
            key: "home",
            icon: <HomeOutlined />,
            label: (
                <a
                    href="/"
                    onClick={(e) => {
                        if (!e.metaKey && !e.ctrlKey) {
                            e.preventDefault();
                            window.location.href = '/';
                        }
                    }}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                >
                    Home
                </a>
            ),
        }] : []),
        {
            type: 'divider',
        },
        {
            key: "schedule",
            icon: <CalendarOutlined />,
            label: "Schedule",
            onClick: () => {
                props.onScheduleClick();
            },
        },
        {
            key: "queries",
            icon: <DatabaseOutlined />,
            label: "Queries",
            onClick: () => {
                props.onQueriesClick();
            },
        },
        {
            key: "settings",
            icon: <SettingOutlined />,
            label: "Settings",
            onClick: () => {
                props.onSettingsClick();
            },
        },
        {
            key: "logout",
            icon: <LogoutOutlined />,
            label: "Log Out",
            onClick: () => {
                props.onLogout();
                window.location.href = '/';
            },
        },
    ];

    return (
        <div style={{
            backgroundColor: themeColors.backgroundColorSecondary,
            borderBottom: `2px solid ${themeColors.borderColor}`,
            padding: "0.5em",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "1em"
        }}>
            <style>{`
                .model-select .ant-select-selection-item {
                    font-weight: bold !important;
                    font-size: 1.3em !important;
                    color: ${themeColors.primaryColor} !important;
                }
                ${darkMode ? `
                    .model-select .ant-select-selector {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .model-select .ant-select-arrow {
                        color: ${themeColors.textColor} !important;
                    }
                    .model-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                    }
                    .model-select-dropdown .ant-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                        border-color: ${themeColors.borderColor} !important;
                        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.48) !important;
                        outline: none !important;
                    }
                    .model-select-dropdown .ant-select-dropdown,
                    .model-select-dropdown .ant-select-dropdown > * {
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .model-select-dropdown .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .model-select-dropdown .ant-select-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .model-select-dropdown .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .model-select-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                ` : ''}
                .ant-select-dropdown ::-webkit-scrollbar {
                    width: 8px !important;
                }
                .ant-select-dropdown ::-webkit-scrollbar-track,
                .ant-select-dropdown ::-webkit-scrollbar-track-piece {
                    background: transparent !important;
                    border: none !important;
                }
                .ant-select-dropdown ::-webkit-scrollbar-thumb {
                    background: #888 !important;
                    border-radius: 4px !important;
                    border-left: 4px solid transparent !important;
                }
                ${darkMode ? `
                    .user-menu-dropdown .ant-dropdown-menu {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item-divider {
                        background-color: ${themeColors.borderColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item-icon {
                        color: ${themeColors.textColor} !important;
                    }
                    .user-menu-dropdown .ant-dropdown-menu-item a {
                        color: ${themeColors.textColor} !important;
                    }
                ` : ''}
            `}</style>
            {props.isLeftPanelCollapsed && props.onExpandLeftPanel && (
                <ConditionalTooltip title="Expand panel">
                    <div onClick={props.onExpandLeftPanel} style={{ cursor: "pointer", lineHeight: 0, display: "flex", alignItems: "center", height: "48px", marginRight: "-0.5em", marginTop: "0.05em" }}>
                        <div style={{ height: "48px", display: "flex", alignItems: "center" }}>
                            <DLogo color={themeColors.primaryColor} viewBox="0 0 550 448" width="55" height="48" />
                        </div>
                    </div>
                </ConditionalTooltip>
            )}
            <ConditionalTooltip title="Select the DashStream model">
                <Select
                    value={props.currentModelAlias}
                    onChange={handleModelChange}
                    size="large"
                    className="model-select"
                    popupMatchSelectWidth={false}
                    popupClassName={darkMode ? "model-select-dropdown" : ""}
                >
                    {dashModels.map(model => (
                        <Select.Option key={model.alias} value={model.alias}>
                            {model.displayName}
                        </Select.Option>
                    ))}
                </Select>
            </ConditionalTooltip>
            {props.selectedJobName && (
                <ConditionalTooltip title={props.selectedJobName}>
                    <div
                        style={{
                            flex: 1,
                            textAlign: "center",
                            fontWeight: "900",
                            fontSize: "1.1em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: themeColors.textColor
                        }}
                    >
                        {props.selectedJobName}
                    </div>
                </ConditionalTooltip>
            )}
            <div style={{ marginLeft: "auto", paddingRight: "0.5em", display: "flex", alignItems: "center", gap: "0.75em" }}>
                {sisterModelAlias && (
                    <ConditionalTooltip title="Switch to QueryStream">
                        <div onClick={handleSwitchToSister} style={{ cursor: "pointer", lineHeight: 0, display: "flex", alignItems: "center", height: "48px", marginRight: "-0.5em" }}>
                            <div style={{ height: "48px", display: "flex", alignItems: "center" }}>
                                <QLogo color={themeColors.primaryColor} viewBox="0 0 566 448" width="55" height="48" />
                            </div>
                        </div>
                    </ConditionalTooltip>
                )}
                <Dropdown
                    trigger={["click"]}
                    menu={{ items: menuItems }}
                    placement="bottomRight"
                    overlayStyle={{ marginTop: "8px" }}
                    overlayClassName={darkMode ? "user-menu-dropdown" : ""}
                >
                    <div style={{ display: "inline-block" }}>
                        <ConditionalTooltip title={props.session.userDisplayName}>
                            <div style={{ cursor: "pointer", lineHeight: 0 }}>
                                {props.session.userPicture ? (
                                    <Avatar src={props.session.userPicture} size={48} />
                                ) : (
                                    <Avatar size={48} icon={<UserOutlined />} style={{ backgroundColor: themeColors.primaryColor }} />
                                )}
                            </div>
                        </ConditionalTooltip>
                    </div>
                </Dropdown>
            </div>
        </div>
    );
}

